import { config } from '@/config'

/**
 * Sesión del portal.
 *
 * El token es el del usuario final: la plataforma aplica sola el recorte de
 * visibilidad y el portal no implementa control de acceso (corrección §1).
 *
 * Dos vías de entrega, detrás de la misma interfaz, para que pasar de una a
 * otra no obligue a refactorizar el front (corrección §4.2):
 *
 *  1. Query param `?token=…` — provisional. Un token en la URL queda en el
 *     historial, en los logs de acceso y en cualquier proxy del camino, así
 *     que se borra de la barra de direcciones apenas se lee.
 *  2. `postMessage` desde el contenedor de Cotalker — definitiva. Se emite
 *     siempre con origen de destino explícito, nunca con comodín, y se valida
 *     el origen de lo que llega contra una lista blanca.
 *
 * El token vive en memoria y no en localStorage: si la pestaña se cierra, se
 * va con ella.
 */

export const MSG_TOKEN = 'esmax-portal:token'
export const MSG_REQUEST = 'esmax-portal:token-request'
export const MSG_EXPIRED = 'esmax-portal:token-expired'

/**
 * Nombre del parámetro con que el contenedor entrega el token:
 *   /?cotalker-token=<jwt>
 * Se acepta `token` como alias para no romper enlaces viejos.
 */
export const TOKEN_PARAM = 'cotalker-token'
const TOKEN_PARAM_ALIASES = [TOKEN_PARAM, 'token']

type Listener = (token: string | null) => void

let token: string | null = null
const listeners = new Set<Listener>()

export const getToken = (): string | null => token
export const hasToken = (): boolean => token !== null

/**
 * Vencimiento del JWT, en milisegundos, o null si no se puede leer.
 *
 * Solo se lee el `exp` del payload: no se valida la firma, que es cosa del
 * servidor. Sirve para dar un mensaje claro en vez de disparar una llamada
 * que ya se sabe que va a volver 401 — los tokens observados duran 1 hora,
 * así que un portal embebido y abierto un rato se topa con esto seguido.
 */
export function tokenExpiryMs(jwt: string | null = token): number | null {
  if (!jwt) return null
  const payload = jwt.split('.')[1]
  if (!payload) return null
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const exp = (JSON.parse(json) as { exp?: unknown }).exp
    return typeof exp === 'number' ? exp * 1000 : null
  } catch {
    return null
  }
}

/** Un token vencido se trata como ausente: no vale la pena gastar la llamada. */
export function isTokenExpired(now = Date.now()): boolean {
  const exp = tokenExpiryMs()
  return exp !== null && exp <= now
}

export function onTokenChange(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function setToken(next: string | null): void {
  if (token === next) return
  token = next
  listeners.forEach((fn) => fn(token))
}

const isTrusted = (origin: string): boolean => config.TRUSTED_ORIGINS.includes(origin)

/** Emite a los orígenes autorizados, uno por uno y siempre explícito. */
function postToContainer(message: unknown): void {
  const targets = new Set<Window>()
  if (window.parent && window.parent !== window) targets.add(window.parent)
  if (window.opener) targets.add(window.opener as Window)
  if (targets.size === 0) return

  for (const target of targets) {
    for (const origin of config.TRUSTED_ORIGINS) {
      // Nunca '*': con comodín el mensaje llega a cualquier origen que
      // esté ocupando el marco en ese momento.
      try {
        target.postMessage(message, origin)
      } catch {
        /* origen no disponible; se intenta con el siguiente */
      }
    }
  }
}

function onMessage(event: MessageEvent): void {
  if (!isTrusted(event.origin)) return
  const data = event.data as { type?: string; token?: unknown } | null
  if (!data || data.type !== MSG_TOKEN) return
  if (typeof data.token === 'string' && data.token.length > 0) setToken(data.token)
}

/** Le pide al contenedor un token fresco. */
export function requestToken(): void {
  postToContainer({ type: MSG_REQUEST })
}

/**
 * El token dejó de servir. Los tokens duran 7 días y el anterior sigue
 * funcionando ~60 s después de renovar, así que no hay que coordinar el
 * cambio con precisión: basta con avisar y pedir uno nuevo.
 */
export function invalidateToken(): void {
  setToken(null)
  postToContainer({ type: MSG_EXPIRED })
  requestToken()
}

let started = false

export function initSession(): void {
  if (started) return
  started = true

  const url = new URL(window.location.href)
  let found: string | null = null
  for (const name of TOKEN_PARAM_ALIASES) {
    const value = url.searchParams.get(name)
    if (value) {
      found ??= value
      url.searchParams.delete(name)
    }
  }
  if (found) {
    setToken(found)
    // Sacarlo de la URL en el acto: cuesta dos líneas y evita que quede en el
    // historial, en los logs de acceso, en proxies y en la cabecera Referer.
    window.history.replaceState(null, '', url.toString())
  }

  window.addEventListener('message', onMessage)
  if (!token) requestToken()
}
